import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ControlledTableInput from '../../form-generator-v2/ControlledTableInput';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  fromCommissionTableRows,
  getCommissionConfigForInventory,
  resetCommissionFieldInFormValues,
  toCommissionTableRows
} from './inventoryCommissionHelpers';

const COMMISSION_SHEET_OPTIONS_TABLE_FIELDS = [
  { title: 'Commission Sheet Option', key: 'key', type: 'datalist-text', required: false },
  { title: 'Value', key: 'value', type: 'number', required: false }
];

const COMMISSION_SHEET_LESS_OPTIONS_TABLE_FIELDS = [
  { title: 'Commission Sheet Less Option', key: 'key', type: 'datalist-text', required: false },
  { title: 'Value', key: 'value', type: 'number', required: false }
];

const getInventoryCommissionContext = inventory => ({
  unitLocationId: inventory?.unitLocation?._id || inventory?.unitLocation,
  numberOfSections: inventory?.numberOfSections
});

const buildCommissionPayload = values => ({
  commissionSheetOptions: fromCommissionTableRows(values.commissionSheetOptions),
  commissionSheetLessOptions: fromCommissionTableRows(values.commissionSheetLessOptions)
});

const InventoryCommissionFields = forwardRef(
  ({ inventory, appChoices, onInventoryUpdate, onValuesChange, skipApiSave = false, disabled = false }, ref) => {
    const [commissionValues, setCommissionValues] = useState({
      commissionSheetOptions: [],
      commissionSheetLessOptions: []
    });
    const latestValuesRef = useRef(commissionValues);

    const { unitLocationId, numberOfSections } = useMemo(
      () => getInventoryCommissionContext(inventory),
      [inventory]
    );

    const dropdownOptions = useMemo(() => {
      const { commissionOptions, commissionLessOptions } = getCommissionConfigForInventory(
        appChoices,
        unitLocationId,
        numberOfSections
      );

      return {
        commissionSheetOptions: commissionOptions.map(({ name }) => [name]),
        commissionSheetLessOptions: commissionLessOptions.map(({ name }) => [name])
      };
    }, [appChoices, unitLocationId, numberOfSections]);

    const notifyValuesChange = values => {
      if (onValuesChange) {
        onValuesChange(buildCommissionPayload(values));
      }
    };

    useEffect(() => {
      const nextValues = {
        commissionSheetOptions: toCommissionTableRows(inventory?.commissionSheetOptions),
        commissionSheetLessOptions: toCommissionTableRows(inventory?.commissionSheetLessOptions)
      };
      latestValuesRef.current = nextValues;
      setCommissionValues(nextValues);
      notifyValuesChange(nextValues);
    }, [inventory?._id, inventory?.commissionSheetOptions, inventory?.commissionSheetLessOptions]);

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          if (!inventory?._id || disabled) return true;

          const payload = buildCommissionPayload(latestValuesRef.current);

          if (skipApiSave) {
            if (onInventoryUpdate) {
              onInventoryUpdate({
                ...inventory,
                ...payload
              });
            }
            return true;
          }

          const { response, error } = await makeApiRequests({
            endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
            requestBody: payload,
            method: 'PUT'
          });

          if (error) {
            toast.error(error);
            return false;
          }

          if (onInventoryUpdate) {
            onInventoryUpdate({
              ...inventory,
              ...response
            });
          }

          return true;
        }
      }),
      [disabled, inventory, onInventoryUpdate, skipApiSave]
    );

    const updateField = (fieldKey, fieldValue) => {
      const nextValues = {
        ...commissionValues,
        [fieldKey]: fieldValue
      };
      latestValuesRef.current = nextValues;
      setCommissionValues(nextValues);
      notifyValuesChange(nextValues);
    };

    const resetFieldToDefault = fieldKey => {
      const nextValues = resetCommissionFieldInFormValues(
        {
          unitLocation: unitLocationId,
          numberOfSections,
          ...commissionValues
        },
        appChoices,
        fieldKey
      );
      latestValuesRef.current = nextValues;
      setCommissionValues(nextValues);
      notifyValuesChange(nextValues);
    };

    if (!inventory?._id) return null;

    return (
      <Row>
        <Col xs={12} md={6} className="mb-3 px-0">
          <ControlledTableInput
            id={`${inventory._id}-commissionSheetOptions`}
            title="Commission Sheet Options"
            tableFields={COMMISSION_SHEET_OPTIONS_TABLE_FIELDS}
            value={commissionValues.commissionSheetOptions}
            onChange={rows => updateField('commissionSheetOptions', rows)}
            dropdownOptions={dropdownOptions.commissionSheetOptions}
            disabled={disabled}
            showHideToggle={false}
            defaultHideOptions={false}
            showResetToDefault
            onResetToDefault={() => resetFieldToDefault('commissionSheetOptions')}
          />
        </Col>
        <Col xs={12} md={6} className="mb-3 px-0">
          <ControlledTableInput
            id={`${inventory._id}-commissionSheetLessOptions`}
            title="Commission Sheet Less Options"
            tableFields={COMMISSION_SHEET_LESS_OPTIONS_TABLE_FIELDS}
            value={commissionValues.commissionSheetLessOptions}
            onChange={rows => updateField('commissionSheetLessOptions', rows)}
            dropdownOptions={dropdownOptions.commissionSheetLessOptions}
            disabled={disabled}
            showHideToggle={false}
            defaultHideOptions={false}
            showResetToDefault
            onResetToDefault={() => resetFieldToDefault('commissionSheetLessOptions')}
          />
        </Col>
      </Row>
    );
  }
);

InventoryCommissionFields.displayName = 'InventoryCommissionFields';

export default InventoryCommissionFields;
